/**
 * Back-to-products control + current category title for Access configurator.
 */

import { ArrowLeft } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { AccessCategoryId } from "@shared/access-configurator";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export interface AccessBackBarProps {
  categoryId: AccessCategoryId;
  onBack: () => void;
  className?: string;
  /** When true, omit outer padding (caller provides it). */
  compact?: boolean;
}

export default function AccessBackBar({
  categoryId,
  onBack,
  className,
  compact = false,
}: AccessBackBarProps) {
  const { t } = useTranslation();

  return (
    <div
      className={cn(
        "flex flex-wrap items-center gap-3",
        !compact && "px-0",
        className
      )}
    >
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={onBack}
        className={cn(
          "h-9 gap-1.5 border-border font-medium shrink-0",
          "hover:border-[hsl(var(--configurator-accent))] hover:text-[hsl(var(--configurator-accent))]",
          "focus-visible:ring-[hsl(var(--configurator-accent))]"
        )}
        style={{ color: "var(--configurator-form-text)" }}
      >
        <ArrowLeft className="h-4 w-4" aria-hidden />
        {t("accessConfigurator.backToCategories")}
      </Button>
      <span
        className="text-sm font-semibold min-w-0"
        style={{ color: "var(--configurator-form-text)" }}
      >
        {t(`accessConfigurator.categories.${categoryId}`)}
      </span>
    </div>
  );
}
